import React, { useCallback, useState, useEffect, useMemo } from "react";
import { DataGrid, Column, MasterDetail } from "devextreme-react/data-grid";
import useAxiosPrivate, { axiosHrPrivate } from "../../../api/useAxios";
import { getPropsDataGridPopup, getPropsLookupExprForIdName } from "../../../utils/default-props.js";
import { Button, Popup } from "devextreme-react";
import DetailTabPanel from "./detail-tab-panel.tsx";
import CustomAddForm from "./custom-add-form.tsx";
import { useDataSource } from "../../../contexts/dataSource";

interface LeaveAccrual {
    id?: number;
    leaveInfoId: number;
    leaveInfoName?: string;
    earnedDate: Date;
    expiryDate: Date;
    deserved: number;
    used: number;
    remaining: number;
}

interface UsedLeave {
    leaveInfoId: number;
    leaveInfoName: string;
    startDate: string;
    endDate: string;
    duration: number;
}

interface CombinedLeaveData {
    leaveInfoId: number;
    leaveInfoName: string;
    deserved: number;
    used: number;
    remaining: number;
    hasUsedLeaves: boolean;
}

interface LeaveAccrualListProps {
    employeeId: number;
}

const LeaveAccrualList: React.FC<LeaveAccrualListProps> = ({ employeeId }) => {
    const [combinedData, setCombinedData] = useState<CombinedLeaveData[]>([]);
    const axiosPrivate = useAxiosPrivate(axiosHrPrivate);
    const [popupVisible, setPopupVisible] = useState(false);
    const { dataSourceHR } = useDataSource();

    //İzin türlerine göre gruplama yapan fonksiyon
    const groupDataByLeaveType = useCallback((accrualData: LeaveAccrual[], usedLeaves: UsedLeave[]) => {
        const leaveTypesMap = new Map<number, CombinedLeaveData>();

        usedLeaves.forEach(item => {
            if (!leaveTypesMap.has(item.leaveInfoId)) {
                leaveTypesMap.set(item.leaveInfoId, {
                    leaveInfoId: item.leaveInfoId,
                    leaveInfoName: item.leaveInfoName || '',
                    deserved: 0,
                    used: item.duration,
                    remaining: 0,
                    hasUsedLeaves: true
                });
            } else {
                const current = leaveTypesMap.get(item.leaveInfoId)!;
                current.used += item.duration;
                current.hasUsedLeaves = true;
            }
        });

        accrualData.forEach(item => {
            if (!leaveTypesMap.has(item.leaveInfoId)) {
                leaveTypesMap.set(item.leaveInfoId, {
                    leaveInfoId: item.leaveInfoId,
                    leaveInfoName: item.leaveInfoName || '',
                    deserved: item.deserved,
                    used: item.used,
                    remaining: item.remaining,
                    hasUsedLeaves: false
                });
            } else {
                const current = leaveTypesMap.get(item.leaveInfoId)!;
                current.deserved += item.deserved;
                current.remaining += item.remaining;
            }
        });

        return Array.from(leaveTypesMap.values());
    }, []);

    const fetchLeaveData = useCallback(async () => {
        try {
            const [accrualResponse, usedResponse] = await Promise.all([
                axiosPrivate.get(`/api/EmployeeLeaveAccrual/Employee/${employeeId}`),
                axiosPrivate.get(`/api/EmployeeLeave/Employee/${employeeId}`)
            ]);

            if (accrualResponse?.status === 200 && usedResponse?.status === 200) {
                const accrualData = accrualResponse.data?.data || [];
                const usedData = usedResponse.data?.data || [];

                /*Çalışanın hak edilmiş bir izni yok ama kullanılmış 
                 bir izni varsa diye listede göstermek için verileri birleştiriyoruz*/
                const combined = groupDataByLeaveType(accrualData, usedData);
                setCombinedData(combined);
            }
        } catch (error) {
            console.error("Error fetching leave data:", error);
        }
    }, [employeeId, axiosPrivate, groupDataByLeaveType]);

    useEffect(() => {
        fetchLeaveData();
    }, [fetchLeaveData]);

    const dbLeaveType = useMemo(() =>
        dataSourceHR({
            loadUrl: "/api/LeaveInfo",
            loadMode: "raw"
        }),
        [dataSourceHR]);

    const handlePopupClose = () => {
        setPopupVisible(false);
        fetchLeaveData();
    };

    return (
        <React.Fragment>
            <div className="content-block">
                <div className="dx-card responsive-paddings">
                    <Button
                        text="İzin Hakediş Ekle"
                        icon="add"
                        onClick={() => setPopupVisible(true)}
                    />
                    <Popup
                        visible={popupVisible}
                        onHiding={handlePopupClose}
                        dragEnabled={true}
                        hideOnOutsideClick={true}
                        showCloseButton={true}
                        {...getPropsDataGridPopup}
                    >
                        <CustomAddForm
                            employeeId={employeeId}
                            onSuccess={() => {
                                handlePopupClose();
                            }}
                        />
                    </Popup>
                    <DataGrid
                        dataSource={combinedData}
                        showBorders={true}
                    >
                        <Column
                            dataField="leaveInfoId"
                            caption="İzin Adı"
                            lookup={{
                                dataSource: dbLeaveType,
                                ...getPropsLookupExprForIdName
                            }}
                        />
                        <Column dataField="deserved" caption="Hak Edilen" dataType="number" />
                        <Column dataField="used" caption="Kullanılan" dataType="number" />
                        <Column dataField="remaining" caption="Kalan" dataType="number" />
                        <MasterDetail
                            enabled={true}
                            component={({ data }) => (
                                <DetailTabPanel
                                    leaveInfoId={data.data?.leaveInfoId}
                                    employeeId={employeeId}
                                />
                            )}
                        />
                    </DataGrid>
                </div>
            </div>
        </React.Fragment>
    );
};

export default LeaveAccrualList;